// src/utils/ArrayUtils.ts
var ArrayUtils = class {
  /**returns the last element of an Array where the condition is true. If no
   * elements match, null is returned
   */
  static lastWhere(items, condition, thisObj) {
    if (!items) {
      return null;
    }
    if (thisObj) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (condition.call(thisObj, items[i])) {
          return items[i];
        }
      }
    } else {
      for (let i = items.length - 1; i >= 0; i--) {
        if (condition(items[i])) {
          return items[i];
        }
      }
    }
    return null;
  }
  /**returns the index of the last element of an Array where the condition is
   * true. If no elements match, -1 is returned
   */
  static lastIndexWhere(items, condition, thisObj) {
    if (!items) {
      return null;
    }
    if (thisObj) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (condition.call(thisObj, items[i])) {
          return i;
        }
      }
    } else {
      for (let i = items.length - 1; i >= 0; i--) {
        if (condition(items[i])) {
          return i;
        }
      }
    }
    return -1;
  }
  static removeWhere(items, condition, thisObj) {
    if (!items) {
      return;
    }
    if (thisObj) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (condition.call(thisObj, items[i])) {
          items.splice(i, 1);
        }
      }
    } else {
      for (let i = items.length - 1; i >= 0; i--) {
        if (condition(items[i])) {
          items.splice(i, 1);
        }
      }
    }
  }
  /**removes the first occurance of @item from the @array .
   * @returns true if an item was removed. */
  static remove(item, array) {
    let idx = array.indexOf(item);
    if (idx !== -1) {
      array.splice(idx, 1);
      return true;
    } else {
      return false;
    }
  }
  /**generates all unique combination permutations from the elements of an
   * array.
   * @param arr The array of elements to create combinations of
   * @param min The minimum number of elements that constitutes a valid combo
   * @param max The maximum number of elements that constitutes a valid combo
   * 
   * ex.
   * input: ([a1, a2, a3], 1, 2)
   * makes: [[a1], [a2], [a3], [a1, a2], [a1, a3], [a2, a3]]
   */
  static generateCombos(arr, min, max) {
    const combinations = [];
    function generate(combination, start) {
      if (combination.length >= min && combination.length <= max) {
        combinations.push([...combination]);
      }
      for (let i = start; i < arr.length; i++) {
        combination.push(arr[i]);
        generate(combination, i + 1);
        combination.pop();
      }
    }
    generate([], 0);
    return combinations;
  }
};

// src/utils/BrowserUtils.ts
var BrowserUtils = class {
  static isSafari() {
    let uagent = window.navigator.userAgent.toLowerCase();
    return uagent.indexOf("safari") !== -1 && uagent.indexOf("chrome") === -1 && uagent.indexOf("android") === -1;
  }
  static isMobile() {
    let u = window.navigator.userAgent.toLowerCase();
    return u.includes("android") || u.includes("webos") || u.includes("iphone") || u.includes("ipad") || u.includes("ipod") || u.includes("blackberry") || u.includes("windows phone");
  }
  static isiOS() {
    let u = window.navigator.userAgent.toLowerCase();
    return u.includes("iphone") || u.includes("ipad") || u.includes("ipod");
  }
  static isAndroid() {
    let u = window.navigator.userAgent.toLowerCase();
    return u.includes("android");
  }
  /**returns the query parameters in the URL - use .get() to retrieve a value
   * by name*/
  static getQueryParams() {
    return new URLSearchParams(location.search);
  }
  /**returns the string value of the query parameter matching the passed in 
   * name. if url is left null, window.location is used.*/
  static getQueryParam(paramName, url) {
    if (url) {
      let idx = url.indexOf("?");
      if (idx === -1) {
        return null;
      }
      return new URLSearchParams(url.slice(idx)).get(paramName);
    } else {
      return new URLSearchParams(location.search).get(paramName);
    }
  }
  /**returns the number value of an integer value from the query string. If 
   * the value doesn't exist or fails to convert as an number, null is returned.
   * If you don't specify params, [Uri.base.queryParameters] is used.*/
  static getQueryParamInt(paramName, params = null) {
    if (params == null)
      params = new URLSearchParams(location.search);
    let param = params.get(paramName);
    if (param == null)
      return null;
    try {
      return Number.parseInt(param);
    } catch (err) {
      return null;
    }
  }
  /**constructs and returns a new query string after removing and adding
   * the requested parameters. This does not update the browser URL (use
   * something like window.history.replaceState({"page":"pageName"}, "", 
   * "./qstring") to achieve this). If there are no query parameters (or
   * all are removed), "" is returned. Note, this will remove any duplicate
   * parameters from the qstring. If you add a value that's already in
   * the query string, it will be updated instead of added.*/
  static getUpdatedQueryString(paramsToRemove, paramsToAdd) {
    let qstring;
    if (paramsToAdd != null) {
      for (let key of paramsToAdd.keys()) {
        if (qstring == null) {
          qstring = `?${key}=${paramsToAdd.get(key)}`;
        } else {
          qstring += `&${key}=${paramsToAdd.get(key)}`;
        }
      }
    }
    let params = new URLSearchParams(location.search);
    for (let key of params.keys()) {
      if ((paramsToRemove == null || paramsToRemove.indexOf(key) === -1) && (paramsToAdd == null || !paramsToAdd.has(key))) {
        if (qstring == null) {
          qstring = `?${key}=${params.get(key)}`;
        } else {
          qstring += `&${key}=${params.get(key)}`;
        }
      }
    }
    return qstring ?? "";
  }
  /**constructs and returns a new query string. If there are no query 
   * parameters, "" is returned. If a key in the params is mapped to a null
   * value, that key/value pair is omitted.*/
  static buildQueryString(params) {
    let qstring;
    if (params != null) {
      for (let key of params.keys()) {
        if (params.get(key) == null) {
          continue;
        }
        if (qstring == null) {
          qstring = `?${key}=${params.get(key)}`;
        } else {
          qstring += `&${key}=${params.get(key)}`;
        }
      }
    }
    return qstring ?? "";
  }
  /**returns the folder path portion of a url that points to a file. If
   * the path includes no slashes, a single "/" is returned.*/
  static getFolderPathToFile(fileUrl) {
    let i = fileUrl.lastIndexOf("/");
    if (i < 0)
      i = fileUrl.lastIndexOf("\\");
    if (i >= 0)
      return fileUrl.substring(0, i + 1);
    return "/";
  }
};

// src/utils/CompressionUtils.ts
import {
  BlobReader,
  BlobWriter,
  TextReader,
  TextWriter,
  Uint8ArrayReader,
  ZipReader,
  ZipWriter
} from "@zip.js/zip.js";

// src/utils/ConversionUtils.ts
var ConversionUtils = class _ConversionUtils {
  static getUint8ArrayFromArrayBuffer(buffer) {
    return new Uint8Array(buffer);
  }
  static getBlobFromArrayBuffer(buffer, type = null) {
    let array = _ConversionUtils.getUint8ArrayFromArrayBuffer(buffer);
    return type == null ? new Blob([array]) : new Blob([array], { type });
  }
  static async getUint8ArrayFromBlob(blob) {
    return new Promise((resolve, reject) => {
      _ConversionUtils.getArrayBufferFromBinaryBlob(blob).then((buffer) => {
        let array = _ConversionUtils.getUint8ArrayFromArrayBuffer(
          buffer
        );
        resolve(array);
      }).catch((err) => {
        reject(err);
      });
    });
  }
  static getNumberArrayFromUint8Array(uint8Array) {
    return Array.from(uint8Array);
  }
  /**returns a string decoded from a decompressed Uint8Array. */
  static getStringFromUint8Array(array) {
    return new TextDecoder().decode(array);
  }
  static getUint8ArrayFromString(text) {
    return new TextEncoder().encode(text);
  }
  /**returns a string read from a Blob (File extends Blob and also works) */
  static getStringFromTextBlob(blob, encoding = "UTF-8") {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = (_) => {
        resolve(reader.result);
      };
      reader.onerror = (ev) => {
        reject(ev.toString());
      };
      reader.readAsText(blob, encoding);
    });
  }
  /**returns an ArrayBuffer read from a Blob (File extends Blob and also
   * works) */
  static getArrayBufferFromBinaryBlob(blob) {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = (_) => {
        resolve(reader.result);
      };
      reader.onerror = (ev) => {
        reject(ev.toString());
      };
      reader.readAsArrayBuffer(blob);
    });
  }
};

// src/utils/CompressionUtils.ts
var CompressionUtils = {
  // https://gildas-lormeau.github.io/zip.js/core-api.html
  /**
   * 
   * @param files A Map of filename/blob pairs
   * @param compression A value from 0 (no compression) to 9. 5 is default
   * @returns A Zip blob
   */
  async zipBlobs(files, compression = 5) {
    try {
      let blobWriter = new BlobWriter("application/zip");
      let zipWriter = new ZipWriter(blobWriter);
      for (let fileName of files.keys()) {
        let textReader = new BlobReader(files.get(fileName));
        await zipWriter.add(fileName, textReader, {
          level: compression
        });
      }
      await zipWriter.close();
      let blob = blobWriter.getData();
      return blob;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  /**
   * Decompresses a zip file to a collection of Entry objects
   * @param file a Blob, ArrayBuffer or Uint8Array of a compressed zip file
   * @returns an array of Entry objects
   */
  async unzip(file) {
    try {
      let reader;
      if (file instanceof Blob) {
        reader = new BlobReader(file);
      } else if (file instanceof ArrayBuffer) {
        let array = ConversionUtils.getUint8ArrayFromArrayBuffer(file);
        reader = new Uint8ArrayReader(array);
      } else if (file instanceof Uint8Array) {
        reader = new Uint8ArrayReader(file);
      }
      let zipReader = new ZipReader(reader);
      let entries = await zipReader.getEntries();
      await zipReader.close();
      return entries;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  async unzipToUint8Array(compressed) {
    try {
      let reader = new Uint8ArrayReader(compressed);
      await reader.init();
      let decompressed = await reader.readUint8Array(
        0,
        compressed.length
      );
      return decompressed;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  async getStringFromEntry(entry) {
    try {
      let writer = new TextWriter();
      let text = await entry.getData(writer);
      return text;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  async getBlobFromEntry(entry) {
    try {
      let writer = new BlobWriter();
      let blob = await entry.getData(writer);
      return blob;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  /**
   *creates a [Blob] object of a text string after compressing it with
   *ZIP compression.
   */
  async createZipFileFromString(text, uncompressedName = "file.txt") {
    try {
      let blobWriter = new BlobWriter("application/zip");
      let zipWriter = new ZipWriter(blobWriter);
      let textReader = new TextReader(text);
      await zipWriter.add(uncompressedName, textReader);
      await zipWriter.close();
      let blob = blobWriter.getData();
      return blob;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  /**converts a [html.Blob] object that was zip compressed into its string
   * contents. This is the counterpart to [createZipFileFromString].*/
  async getStringFromZipBlob(zipBlob) {
    let entries = await CompressionUtils.unzip(zipBlob);
    if (entries != null) {
      let text = await CompressionUtils.getStringFromEntry(entries[0]);
      return text;
    }
  }
};

// src/utils/JsonUtils.ts
var JsonUtils = class {
  static decode(source) {
    try {
      return JSON.parse(source);
    } catch (err) {
      console.log(err);
      return null;
    }
  }
  static decodeAsList(source) {
    try {
      return JSON.parse(source);
    } catch (err) {
      console.log(err);
      return null;
    }
  }
  static decodeAsMapOfStringKeys(source) {
    try {
      return JSON.parse(source);
    } catch (err) {
      console.log(err);
      return null;
    }
  }
  static encode(value) {
    try {
      return JSON.stringify(value);
    } catch (err) {
      console.log(err);
      return null;
    }
  }
  static jsArrayToArray(array) {
    let list = [];
    for (let el of array) {
      list.push(el);
    }
    return list;
  }
};

// src/utils/StringUtils.ts
var StringUtils = class {
  static isString(obj) {
    return typeof obj === "string" || obj instanceof String;
  }
  static generateRandomString(length) {
    var result = "";
    var characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let numCharacters = characters.length;
    for (let i = 0; i < length; i++) {
      let idx = Math.trunc(Math.random() * numCharacters);
      result += characters[idx];
    }
    return result;
  }
  /**returns true if @str IS null or undefined but NOT empty */
  static isNull(str) {
    return str == null;
  }
  /**returns true if @str is NOT null but IS empty */
  static isEmpty(str) {
    return str != null && str.length === 0;
  }
  /**returns true if @str is null, undefined or empty */
  static isNullOrEmpty(str) {
    return !str || str.length === 0;
  }
  /**returns true if @str is not null or undefined (but may be empty) */
  static isNotNull(str) {
    return str != null;
  }
  /**returns true if @str is not null, undefined or empty. If false, it is
   * empty or null/undefined. */
  static isNotEmpty(str) {
    return str != null && str.length > 0;
  }
};

// src/utils/dtos.ts
var IDTO = class {
};
var RdtoMessage = class {
};
RdtoMessage.ALREADY_EXISTS = "already exists";
RdtoMessage.NOT_FOUND = "not found";
RdtoMessage.SERVER_ERROR = "server error";
RdtoMessage.UNAUTHORIZED = "unauthorized";
var RDTO = class {
  constructor(json, generator) {
    /**set to true if the response JSON was properly formatted */
    this.success = false;
    /**/
    //////////do not use these for public API//////////////*/
    // ignore: unused_field
    this._jsonParsed = false;
    this._generator = generator;
    if (json != null) {
      let isString = StringUtils.isString(json);
      let jsonMap = isString ? JsonUtils.decode(json) : json;
      this._parse(jsonMap);
    }
  }
  _parse(json) {
    if (json == null || !("success" in json)) {
      return;
    }
    this._jsonParsed = true;
    this.success = json["success"];
    this.message = json["message"];
    if (this.success && "data" in json) {
      let dataValue = json["data"];
      if (StringUtils.isString(dataValue)) {
        dataValue = JsonUtils.decode(dataValue);
        if (dataValue == null) {
          console.log(`invalid json object?: ${dataValue}`);
          return;
        }
      }
      if (dataValue instanceof Array) {
        this.dataList = [];
        for (let d of dataValue) {
          let dto = this._generator();
          dto.parse(d);
          this.dataList.push(dto);
        }
      } else {
        this.data = this._generator();
        this.data.parse(dataValue);
      }
    }
  }
};
var MarkerRDTO = class extends RDTO {
  constructor(json, generator) {
    super(json, generator);
  }
  _parse(json) {
    super._parse(json);
    if (json != null) {
      this.marker = json["marker"];
    }
  }
};
var EmptyDTO = class extends IDTO {
  parse(json) {
  }
};

// src/utils/geometry.ts
var Point = class {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
};

// src/utils/HtmlUtils.ts
var HtmlUtils = class {
  /**removes all Option elements from a Select element */
  static removeOptionsFromSelect(el) {
    for (let i = el.options.length - 1; i >= 0; i--) {
      el.options[i].remove();
    }
  }
};

// src/utils/HttpUtils.ts
var HttpUtils = class _HttpUtils {
  static get(url, {
    userToken = null,
    responseType = "text"
  }) {
    return new Promise((resolve, reject) => {
      let request = new XMLHttpRequest();
      request.open("GET", url);
      if (userToken) {
        request.setRequestHeader(
          "Authorization",
          `Bearer ${userToken}`
        );
      }
      request.responseType = responseType;
      request.onload = (e) => {
        request.onload = null;
        request.onerror = null;
        resolve(request);
      };
      request.onerror = (e) => {
        request.onload = null;
        request.onerror = null;
        reject(request);
      };
      request.send();
    });
  }
  /**Shortcut method for retrieving string content from GET call. If the
   * request fails, null is returned*/
  static async getString(url, userToken = null) {
    try {
      let request = await _HttpUtils.get(url, {
        userToken,
        responseType: "text"
      });
      return request.responseText;
    } catch (err) {
      return null;
    }
  }
  /**Shortcut method for retrieving binary content from GET call. If the
   * request fails, null is returned*/
  static async getArrayBuffer(url, userToken = null) {
    try {
      let request = await _HttpUtils.get(url, {
        userToken,
        responseType: "arraybuffer"
      });
      return request.response;
    } catch (err) {
      return null;
    }
  }
  /**Shortcut method for retrieving binary content from GET call. If the
   * request fails, null is returned*/
  static async getBinaryBlob(url, mediaType, userToken = null) {
    try {
      let request = await _HttpUtils.get(url, {
        userToken,
        responseType: "arraybuffer"
      });
      let ab = request.response;
      return ConversionUtils.getBlobFromArrayBuffer(ab, mediaType);
    } catch (err) {
      return null;
    }
  }
  /**Performs a post operation as multipart/form-data if [formData] is
   * specified or as application/json content if either a JSON encoded string 
   * or [dynamic] object is specified for the [json] parameter. The 
   * [userToken] can be used to set a JWT token for authorization. The
   * [responseType] should be one of the following:
   * 'text' (default), 'arraybuffer', 'blob', 'document', 'json'.
   * [method] should be either "POST" (default), "PUT" or "PATCH". Any other
   * value will throw an error.
   * returns a [Promise] that resolves to the [XMLHttpRequest] upon 
   * completion.*/
  static post(url, {
    formData = null,
    json = null,
    userToken = null,
    responseType = "text",
    method = "POST"
  }) {
    if (method !== "POST" && method != "PUT" && method != "PATCH") {
      throw 'method must be "POST", "PATCH" or "PUT"';
    }
    return new Promise((resolve, reject) => {
      let encodedJson;
      if (json != null) {
        encodedJson = StringUtils.isString(json) ? json : JsonUtils.encode(json);
      }
      let request = new XMLHttpRequest();
      request.open(method, url);
      if (userToken) {
        request.setRequestHeader(
          "Authorization",
          `Bearer ${userToken}`
        );
      }
      if (encodedJson) {
        request.setRequestHeader(
          "Content-Type",
          "application/json; charset=UTF-8"
        );
      }
      request.responseType = responseType;
      request.onload = (e) => {
        request.onload = null;
        request.onerror = null;
        resolve(request);
      };
      request.onerror = (e) => {
        request.onload = null;
        request.onerror = null;
        reject(request);
      };
      request.send(formData ?? encodedJson);
    });
  }
  static delete(url, {
    userToken = null,
    responseType = "text"
  }) {
    return new Promise((resolve, reject) => {
      let request = new XMLHttpRequest();
      request.open("DELETE", url);
      if (userToken) {
        request.setRequestHeader(
          "Authorization",
          `Bearer ${userToken}`
        );
      }
      request.responseType = responseType;
      request.onload = (e) => {
        request.onload = null;
        request.onerror = null;
        resolve(request);
      };
      request.onerror = (e) => {
        request.onload = null;
        request.onerror = null;
        reject(request);
      };
      request.send();
    });
  }
  static parseQueryString(queryString = window.location.search) {
    const searchParams = new URLSearchParams(queryString);
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      if (params.hasOwnProperty(key)) {
        if (Array.isArray(params[key])) {
          params[key].push(value);
        } else {
          params[key] = [params[key], value];
        }
      } else {
        params[key] = value;
      }
    }
    return params;
  }
};

// src/utils/IdxDbManager.ts
var IdxDbManager = class _IdxDbManager {
  /**example of [_config]
   *  ```
   *  {
   *  	//a unique db name within this domain (could have multiple)
   *  	"dbName": "odogyDb", 
   *  	//increment when creating/changing objectstores
   *  	"version": 1, 
   *  	//will remove/recreate stores on version increment
   *  	"deleteOldVersionStores": false,
   *  	//similar to tables
   *  	"objectStores": [ 
   *  		{
   *  			//unique name for this store
   *  			"name": "soundfonts",
   *  			//primary key name - used in operations
   *  			"keypath": "id",
   *  			//optionally automatically generate/increment that prim key
   *  			"autoIncrement": false
   *  			//similar to additional table columns
   *  			"indexes": [
   *  				{
   *  					"name" : "version",
   *  					"keypath" : "version",
   *  					"unique" : false
   *  				},
   *  				{
   *  					"name" : "blob",
   *  					"keypath" : "blob",
   *  					"unique" : false
   *  				},
   *  				//additional columns
   *  			]
   *  		},
   *  		//additional object stores
   *  	]
   *  };
   *  ```*/
  constructor(config) {
    this._dbState = 1 /* closed */;
    this._initializeDatabase = (e) => {
      let request = e.target;
      let db = request.result;
      let deleteOld = this._config["deleteOldVersionStores"];
      for (let os of this._config["objectStores"]) {
        if (db.objectStoreNames.contains(os["name"])) {
          if (deleteOld) {
            db.deleteObjectStore(os["name"]);
          } else {
            continue;
          }
        }
        let autoIncrement = os["autoIncrement"] === true;
        let store = db.createObjectStore(os["name"], {
          keyPath: os["keypath"],
          autoIncrement
        });
        for (let idx of os["indexes"]) {
          store.createIndex(idx["name"], idx["keypath"], {
            unique: idx["unique"]
          });
        }
      }
    };
    this._config = config;
    if (!window.indexedDB) {
      window.indexedDB = window.webkitIndexedDB || window.mozIndexedDB;
    }
    if (!window.IDBTransaction) {
      window.IDBTransaction = //@ts-ignore
      window.webkitIDBTransaction || window.msIDBTransaction;
    }
    if (!window.IDBKeyRange) {
      window.IDBKeyRange = //@ts-ignore
      window.webkitIDBKeyRange || window.msIDBKeyRange;
    }
  }
  /**Opens a database for operations. This must be called before using
   * other methods in the [IdxDbManager].*/
  open() {
    return new Promise((resolve, reject) => {
      let response = new IdxDbManagerResponse();
      if (!_IdxDbManager.isSupported) {
        response.success = false;
        response.error = "IndexedDb not supported.";
        resolve(response);
      }
      let openRequest = window.indexedDB.open(
        this._config["dbName"],
        this._config["version"]
      );
      openRequest.onerror = (e) => {
        openRequest.onerror = null;
        openRequest.onsuccess = null;
        response.error = openRequest.error;
        response.success = false;
        resolve(response);
      };
      openRequest.onsuccess = (e) => {
        openRequest.onerror = null;
        openRequest.onsuccess = null;
        this._db = openRequest.result;
        this._dbState = 0 /* open */;
        this._loadFromDb();
        response.success = true;
        resolve(response);
      };
      openRequest.addEventListener(
        "upgradeneeded",
        this._initializeDatabase
      );
    });
  }
  /**Adds a new record to an object store. For example, to an object store
   * called 'soundfonts' (the [objectStoreName]) we might add this [record]:
   * ```
   * 	{ 
   * 		"id": "gUserGsOgg", 
   * 		"filename": "gUserGsMS1442_ogg.zip",
   * 		"version": _soundfontVersion,
   * 		"blob": blob
   * 	}
   * ```*/
  addRecord(objectStoreName, record) {
    if (this._dbState !== 0 /* open */)
      throw "Call IdxDbManager.open() before using methods to add or manipulate records";
    return new Promise((resolve, reject) => {
      let response = new IdxDbManagerResponse();
      try {
        let transaction = this._db.transaction(
          objectStoreName,
          "readwrite"
        );
        let objectStore = transaction.objectStore(objectStoreName);
        let request = objectStore.add(record);
        this._awaitTransactionResult(transaction, request).then((_) => {
          response.addedKey = request.result;
          response.record = record;
          response.success = true;
          resolve(response);
        }).catch((err) => {
          response.success = false;
          response.error = err;
          resolve(response);
        });
      } catch (err) {
        response.success = false;
        response.error = err;
        resolve(response);
      }
    });
  }
  /**Updates the contents of a record in an object store. If the record does
   * not exist, it is added. */
  updateRecord(objectStoreName, record) {
    if (this._dbState !== 0 /* open */)
      throw "Call IdxDbManager.open() before using methods to add or manipulate records";
    return new Promise((resolve, reject) => {
      let response = new IdxDbManagerResponse();
      try {
        let transaction = this._db.transaction(
          objectStoreName,
          "readwrite"
        );
        let objectStore = transaction.objectStore(objectStoreName);
        let request = objectStore.put(record);
        this._awaitTransactionResult(transaction, request).then((_) => {
          response.addedKey = request.result;
          response.record = record;
          response.success = true;
          resolve(response);
        }).catch((err) => {
          response.success = false;
          response.error = err;
          resolve(response);
        });
      } catch (err) {
        response.success = false;
        response.error = err;
        resolve(response);
      }
    });
  }
  /**Removes a record from an object store.*/
  deleteRecord(objectStoreName, key) {
    if (this._dbState !== 0 /* open */)
      throw "Call IdxDbManager.open() before using methods to add or manipulate records";
    return new Promise((resolve, reject) => {
      let response = new IdxDbManagerResponse();
      try {
        let transaction = this._db.transaction(
          objectStoreName,
          "readwrite"
        );
        let objectStore = transaction.objectStore(objectStoreName);
        let request = objectStore.delete(key);
        this._awaitTransactionResult(transaction, request).then((_) => {
          response.success = true;
          resolve(response);
        }).catch((err) => {
          response.success = false;
          response.error = err;
          resolve(response);
        });
      } catch (err) {
        response.success = false;
        response.error = err;
        resolve(response);
      }
    });
  }
  /**Removes all records from an object store*/
  deleteAllRecords(objectStoreName) {
    if (this._dbState !== 0 /* open */)
      throw "Call IdxDbManager.open() before using methods to add or manipulate records";
    return new Promise((resolve, reject) => {
      let response = new IdxDbManagerResponse();
      try {
        let transaction = this._db.transaction(
          objectStoreName,
          "readwrite"
        );
        let objectStore = transaction.objectStore(objectStoreName);
        let request = objectStore.clear();
        this._awaitTransactionResult(transaction, request).then((_) => {
          response.success = true;
          resolve(response);
        }).catch((err) => {
          response.success = false;
          response.error = err;
          resolve(response);
        });
      } catch (err) {
        response.success = false;
        response.error = err;
        resolve(response);
      }
    });
  }
  /**Retrieves a record from a store. Get the record from the
   * [IdxDbManagerResponse.record] property.*/
  getRecord(objectStoreName, key) {
    if (this._dbState !== 0 /* open */)
      throw "Call IdxDbManager.open() before using methods to add or manipulate records";
    return new Promise((resolve, reject) => {
      let response = new IdxDbManagerResponse();
      try {
        let transaction = this._db.transaction(
          objectStoreName,
          "readonly"
        );
        let objectStore = transaction.objectStore(objectStoreName);
        let request = objectStore.get(key);
        this._awaitTransactionResult(transaction, request).then((_) => {
          response.record = request.result;
          response.success = true;
          resolve(response);
        }).catch((err) => {
          response.success = false;
          response.error = err;
          resolve(response);
        });
      } catch (err) {
        response.success = false;
        response.error = err;
        resolve(response);
      }
    });
  }
  /**Returns an [IdxDbManagerResponse] with the records property holding a
   * [List<dynamic>] record objects.*/
  getAllRecords(objectStoreName) {
    if (this._dbState !== 0 /* open */)
      throw "Call IdxDbManager.open() before using methods to add or manipulate records";
    return new Promise((resolve, reject) => {
      let response = new IdxDbManagerResponse();
      try {
        let transaction = this._db.transaction(
          objectStoreName,
          "readonly"
        );
        let objectStore = transaction.objectStore(objectStoreName);
        let request = objectStore.getAll();
        this._awaitTransactionResult(transaction, request).then((_) => {
          response.records = request.result;
          response.success = true;
          resolve(response);
        }).catch((err) => {
          response.success = false;
          response.error = err;
          resolve(response);
        });
      } catch (err) {
        response.success = false;
        response.error = err;
        resolve(response);
      }
    });
  }
  closeDatabase() {
    if (this._dbState === 0 /* open */) {
      this._db.close();
      this._dbState = 1 /* closed */;
    }
  }
  /**attempts to remove database. This may not work properly, so consider
   * just updating to a new version of the database instead.*/
  deleteDatabase(dbName) {
    return new Promise((resolve, reject) => {
      let response = new IdxDbManagerResponse();
      let finish = (success, error) => {
        response.success = success;
        response.error = error;
        resolve(response);
      };
      try {
        if (this._db && this._dbState === 0 /* open */) {
          this.closeDatabase();
        }
        let request = window.indexedDB.deleteDatabase(dbName);
        request.onblocked = (e) => {
          finish(false, "database deletion blocked");
        };
        request.onerror = (e) => {
          finish(false, request.error);
        };
        request.onsuccess = (e) => {
          finish(true);
        };
      } catch (err) {
        finish(false, err);
      }
    });
  }
  _loadFromDb() {
  }
  _awaitTransactionResult(transaction, request) {
    return new Promise((resolve, reject) => {
      let transactionComplete = false;
      let addSuccessful = false;
      let checkFinished = () => {
        if (transactionComplete && addSuccessful) {
          resolve();
        }
      };
      transaction.oncomplete = (e) => {
        transactionComplete = true;
        checkFinished();
      };
      transaction.onerror = (e) => {
        reject(transaction.error);
      };
      request.onsuccess = (e) => {
        addSuccessful = true;
        checkFinished();
      };
      request.onerror = (e) => {
        reject(request.error);
      };
    });
  }
  /**returns true if indexedDb is supported in the current browser*/
  static get isSupported() {
    return !!(window.indexedDB || //@ts-ignore
    window.webkitIndexedDB || window.mozIndexedDB);
  }
};
var IdxDbManagerResponse = class {
};

// src/utils/ts-typed-events.ts
var BaseEvent = class {
  /**
   * Marked as protected to discourage creation outside of
   * createEventEmitter().
   */
  constructor() {
    /** All the current listeners for this event. */
    this.listeners = [];
  }
  /**
   * Attaches a callback to trigger on all emits for this event.
   *
   * @param callback - The callback to invoke on all emits.
   */
  on(callback) {
    this.listeners.push({
      once: false,
      callback
    });
  }
  /**
   * Attaches a callback to trigger on only the first emit for this event.
   *
   * This version either takes a callback or returns a promise.
   *
   * @param callback - Optional callback, if specified invokes the callback
   * only once when the event is triggered, then removes it.
   * Otherwise returns a promise that resolves with the value the next time
   * this event is triggered.
   * @returns Nothing if a callback is passed, otherwise a Promise that
   * should resolve once this Event emits.
   */
  once(callback) {
    if (!callback) {
      const promise = new Promise((resolve) => {
        this.once(resolve);
      });
      this.listeners[this.listeners.length - 1].promise = promise;
      return promise;
    }
    this.listeners.push({
      once: true,
      callback
    });
  }
  /**
   * Removes a callback from this event (regardless of once vs on).
   *
   * Returns true if a callback was removed, false otherwise.
   *
   * @param listener - The callback to remove.
   * @returns True if a callback was removed, false otherwise.
   */
  off(listener) {
    const originalLength = this.listeners.length;
    this.listeners = this.listeners.filter((l) => {
      return listener !== l.callback && (!l.promise || listener !== l.promise);
    });
    if (this.listeners.length < originalLength - 1) {
      console.log("extra callback registered: " + listener.toString());
    }
    return this.listeners.length !== originalLength;
  }
  /**
   * Removes ALL callbacks from this event, regardless of once vs on.
   *
   * Returns the number of listeners removed.
   *
   * @returns The number of listeners removed.
   */
  offAll() {
    const originalLength = this.listeners.length;
    this.listeners.length = 0;
    return originalLength;
  }
};
var SealedEvent = class extends BaseEvent {
  // functionally identical to BaseEvent, just a different name to better
  // signify its difference from the old `Event`.
};
function createEmitterWithBaseEvent(event) {
  const publicListenersEvent = event;
  function emit(emitting) {
    const { listeners } = publicListenersEvent;
    const hadListeners = listeners.length > 0;
    for (const listener of listeners) {
      listener.callback(emitting);
    }
    publicListenersEvent.listeners = listeners.filter(({ once }) => !once);
    return hadListeners;
  }
  const func = emit;
  func.event = event;
  func.emit = emit;
  const emitter = func;
  return emitter;
}
function createEmitter() {
  const EventClass = SealedEvent;
  return createEmitterWithBaseEvent(new EventClass());
}
var Event = class extends BaseEvent {
  /**
   * Creates a new Event, with its emit accessible as a member function.
   */
  constructor() {
    super();
    /**
     * Emits a value to all the listeners, triggering their callbacks.
     * Returns true if the event had listeners emitted to,
     * false otherwise.
     * Because this exists on the event, any code with access to this event
     * can trigger the callback for all listeners.
     *
     * @param emitting - If the Event has a type, this is the data of that type
     * to emit to all listeners. If no type (undefined) this argument should
     * be omitted.
     * @returns True if the event had listeners emitted to, false otherwise.
     */
    this.emit = createEmitterWithBaseEvent(this);
  }
};
function createEventEmitter() {
  return createEmitterWithBaseEvent(new Event());
}

// src/utils/ValidationUtils.ts
var ValidationUtils = class {
  static helloWorld() {
    console.log("hello");
  }
  static isValidEmail(email) {
    let p2 = String.raw``;
    let p = String.raw`^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]` + String.raw`+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.` + String.raw`[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$`;
    let regExp = new RegExp(p);
    return regExp.test(email);
  }
  /**Returns true if a string contains only letters, numbers, and these
   * characters: ,._-**/
  static isValidDisplayName(name) {
    let p = /^[a-zA-Z]+[a-zA-Z0-9\.\-_' ]+$/;
    return p.test(name);
  }
  /**Returns true if a string starts with a letter and contains only letters
   * and numbers.*/
  static isValidUsername(name) {
    let p = /^[a-zA-Z]+[a-zA-Z0-9]+$/;
    return p.test(name);
  }
  static validatePassword(password, minLength = 8, requireUpperAndLower = true, requireNumbers = true, requireNonAlphas = false) {
    let result = new PasswordValidationResult();
    result.pass = true;
    if (password.length < minLength) {
      result.pass = false;
      result.message = `Password must be at least ${minLength} characters.`;
    }
    if (result.pass && requireUpperAndLower) {
      let l = /[a-z]+/;
      let u = /[A-Z]+/;
      if (!l.test(password) || !u.test(password)) {
        result.pass = false;
        result.message = "Password must contain upper and lower case letters";
      }
    }
    if (result.pass && requireNumbers) {
      let p = /\d+/;
      if (!p.test(password)) {
        result.pass = false;
        result.message = "Password must contain at least one number.";
      }
    }
    if (result.pass && requireNonAlphas) {
      let p = /\W+/;
      if (!p.test(password)) {
        result.pass = false;
        result.message = "Password must contain at least one character that isn't a number or letter.";
      }
    }
    return result;
  }
  static removeAllHtmlTags(htmlText) {
    let p = /<[^>]*>/mg;
    return htmlText.replace(p, "");
  }
};
var PasswordValidationResult = class {
};
export {
  ArrayUtils,
  BaseEvent,
  BrowserUtils,
  CompressionUtils,
  ConversionUtils,
  EmptyDTO,
  Event,
  HtmlUtils,
  HttpUtils,
  IDTO,
  IdxDbManager,
  IdxDbManagerResponse,
  JsonUtils,
  MarkerRDTO,
  PasswordValidationResult,
  Point,
  RDTO,
  RdtoMessage,
  SealedEvent,
  StringUtils,
  ValidationUtils,
  createEmitter,
  createEventEmitter
};
